import { useCallback, useState } from 'react';

import { APPLE_SERVICES_ID, getAppleWebRedirectUri } from '../../../config/environment';
import { useAuth } from '../../../contexts/AuthContext';
import { useSampleData } from '../../../contexts/SampleDataContext';
import {
  completeAppleAuthSession,
  formatAppleFullName,
  isAppleLoginCanceled,
  showAppleLoginError,
} from '../appleAuthHelpers';
import type { AppleLoginState } from '../appleAuthTypes';
import { authService } from '../services/authService';

const APPLE_JS_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

type AppleName = {
  firstName?: string | null;
  lastName?: string | null;
};

type AppleSignInSuccess = {
  authorization: {
    id_token: string;
    code?: string;
  };
  user?: {
    email?: string;
    name?: AppleName;
  };
};

type AppleAuthClient = {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<AppleSignInSuccess>;
};

type AppleIDGlobal = {
  auth: AppleAuthClient;
};

const APPLE_SCRIPT_ID = 'apple-auth-sdk';

type AppleScriptEl = {
  id: string;
  src: string;
  async: boolean;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  addEventListener: (
    type: string,
    listener: () => void,
    options?: { once?: boolean },
  ) => void;
};

function getAppleAuth(): AppleAuthClient | null {
  const appleId = (globalThis as { AppleID?: AppleIDGlobal }).AppleID;
  return appleId?.auth ?? null;
}

function loadAppleJsSdk(): Promise<AppleAuthClient> {
  const existing = getAppleAuth();
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const doc = (globalThis as {
      document?: {
        getElementById: (id: string) => AppleScriptEl | null;
        createElement: (tag: string) => AppleScriptEl;
        head: { appendChild: (el: AppleScriptEl) => void };
      };
    }).document;
    if (!doc) {
      reject(new Error('Apple Sign-In is unavailable'));
      return;
    }

    const finish = () => {
      const auth = getAppleAuth();
      if (auth) {
        resolve(auth);
        return;
      }
      reject(new Error('Apple Sign-In is unavailable'));
    };

    const prior = doc.getElementById(APPLE_SCRIPT_ID);
    if (prior) {
      if (getAppleAuth()) {
        finish();
        return;
      }
      prior.addEventListener('load', finish, { once: true });
      prior.addEventListener(
        'error',
        () => reject(new Error('Apple Sign-In is unavailable')),
        { once: true },
      );
      return;
    }

    const script = doc.createElement('script');
    script.id = APPLE_SCRIPT_ID;
    script.src = APPLE_JS_SRC;
    script.async = true;
    script.onload = finish;
    script.onerror = () => reject(new Error('Apple Sign-In is unavailable'));
    doc.head.appendChild(script);
  });
}

export function useAppleLogin(): AppleLoginState {
  const { login } = useAuth();
  const { exitSampleMode, markPreviewSeen } = useSampleData();
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const isAppleAvailable = Boolean(APPLE_SERVICES_ID);

  const handleAppleLogin = useCallback(async () => {
    if (!isAppleAvailable) {
      return;
    }

    setIsAppleLoading(true);
    try {
      const appleAuth = await loadAppleJsSdk();
      appleAuth.init({
        clientId: APPLE_SERVICES_ID,
        scope: 'name email',
        redirectURI: getAppleWebRedirectUri(),
        usePopup: true,
      });

      const response = await appleAuth.signIn();
      const identityToken = response.authorization?.id_token;
      if (!identityToken) {
        throw new Error('Apple Sign-In did not return an identity token');
      }

      const authResponse = await authService.appleAuth({
        identityToken,
        authorizationCode: response.authorization.code,
        email: response.user?.email,
        fullName: formatAppleFullName({
          givenName: response.user?.name?.firstName,
          familyName: response.user?.name?.lastName,
        }),
      });

      await completeAppleAuthSession({
        authResponse,
        login,
        exitSampleMode,
        markPreviewSeen,
      });
    } catch (error) {
      if (isAppleLoginCanceled(error)) {
        return;
      }
      showAppleLoginError(error);
    } finally {
      setIsAppleLoading(false);
    }
  }, [exitSampleMode, isAppleAvailable, login, markPreviewSeen]);

  return { handleAppleLogin, isAppleLoading, isAppleAvailable };
}
